import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  APP_CONTAINER_ROUTES,
  APP_ROUTES,
  ORDER_ROUTES,
} from '@/navigators/routes';
import { Avatar, Props as AvatarProps } from '@/components/avatar';
import {
  AVATAR_DIAMETER,
  CASEY_ID,
  LOADING_MESSAGE_ID,
  MAX_COMPOSER_HEIGHT,
  MIN_COMPOSER_HEIGHT,
  MIN_INPUT_TOOLBAR_HEIGHT,
} from './constants';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  BubbleProps,
  DayProps,
  GiftedChat,
  MessageImageProps,
  MessageProps,
  MessageTextProps,
  SystemMessageProps,
  User,
} from 'react-native-gifted-chat';
import { CannedQuestionList } from './chatbot/canned-question-list';
import { CannedQuestionListLayoutContainer } from './chatbot/canned-question-list-layout-container';
import { CaseyAvatar } from '@/components/casey-avatar';
import { ChatBubble } from './chat-bubble';
import { ChatDay } from './chat-day';
import { ChatEmptyCard } from './chat-empty-card';
import { ChatInputToolbar } from './chat-input-toolbar';
import { ChatMessage } from './chat-message';
import { ChatMessageFile } from './chat-message-file';
import { ChatMessageImage } from './chat-message-image';
import { ChatMessageText } from './chat-message-text';
import { ChatSystemMessage } from './chat-system-message';
import { createStyles } from '@/contexts/theme-context';
import {
  Keyboard,
  Platform,
  ScrollViewProps,
  View,
  ViewStyle,
} from 'react-native';
import { Message } from './types';
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import { OrderDetailComponent } from '@/screens/order-detail/order-detail-component';
import { PEPPER_SUPPORT_UUID } from '@/constants';
import { SCREEN_PADDING_LARGE } from '@/styles';
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';

export interface Props {
  isCaseyAi?: boolean;
  isDisplayingPrompts?: boolean;
  isLoadingEarlier?: boolean;
  isSendingChatbotMessage: boolean;
  // Any additional props to pass to the underlying FlatList component
  listViewProps?: Partial<ScrollViewProps>;
  loadEarlier?: boolean;
  loading: boolean;
  messages: Message[];
  onLoadEarlier?: () => void;
  onPressPrompt: (text: string) => void;
  onSend: (message: Message) => Promise<Message | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[] | null>>;
  setShowCannedQuestionActionSheet: (show: boolean) => void;
  showCannedQuestionActionSheet: boolean;
  supplierEmployeeUUIDs: string[];
  user: User;
}

interface SupplierMapType {
  [employeeUUID: string]: boolean;
}

export const Chat: React.FC<Props> = ({
  isCaseyAi = false,
  isDisplayingPrompts = false,
  isLoadingEarlier = false,
  isSendingChatbotMessage,
  listViewProps,
  loadEarlier = false,
  loading,
  messages,
  onLoadEarlier,
  onPressPrompt,
  onSend,
  setMessages,
  setShowCannedQuestionActionSheet,
  showCannedQuestionActionSheet,
  supplierEmployeeUUIDs,
  user,
}) => {
  const bottomTabBarHeight = useBottomTabBarHeight();
  const safeAreaInsets = useSafeAreaInsets();
  const { showErrorToast } = useToasts();
  const { navigate } = useNavigation<NavigationProp<ParamListBase>>();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const [styles] = useStyles();

  const [selectedOrder, setSelectedOrder] = useState<{
    customerFacingID: string;
    uuid: string;
  } | null>(null);

  const supplierUserMap = useMemo<SupplierMapType>(() => {
    return supplierEmployeeUUIDs.reduce(
      (acc: SupplierMapType, employeeUUID) => {
        acc[employeeUUID] = true;
        return acc;
      },
      {},
    );
  }, [supplierEmployeeUUIDs]);

  // Updates a message in component state with the given identifier
  const updateMessage = (
    _id: string | number,
    getUpdatedMessage: (previousMessage: Message) => Message,
  ) => {
    setMessages((previousMessages) => {
      const messageIndex = (previousMessages || []).findIndex(
        (m) => m._id === _id,
      );
      if (messageIndex === -1) {
        return previousMessages;
      }

      return [
        ...previousMessages!.slice(0, messageIndex),
        // Get the updated message with a shallow copy of the existing one
        getUpdatedMessage({ ...previousMessages![messageIndex] }),
        ...previousMessages!.slice(messageIndex + 1),
      ];
    });
  };

  const sendMessage = async (draftMessage: Message) => {
    let sentMessage: Message | null = null;
    try {
      sentMessage = await onSend(draftMessage);
    } catch (e) {
      showErrorToast();
    }

    updateMessage(draftMessage._id, (previousMessage) => {
      if (sentMessage) {
        // Update the message with the ID generated on the server
        previousMessage._id = sentMessage._id;
      } else {
        previousMessage.error = true;
      }

      return previousMessage;
    });
  };

  return (
    <>
      <View style={styles.base} testID="chat-container">
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator testID="loading-indicator" />
          </View>
        ) : (
          <GiftedChat<Message>
            bottomOffset={Platform.select({
              android: safeAreaInsets.bottom,
              ios: isSmallLayout ? bottomTabBarHeight : undefined,
            })}
            isLoadingEarlier={isLoadingEarlier}
            keyboardShouldPersistTaps="handled"
            listViewProps={{
              ...listViewProps,
              // Dismiss the keyboard when the user starts to scroll the list
              onScrollBeginDrag: Keyboard.dismiss,
            }}
            loadEarlier={loadEarlier}
            maxComposerHeight={MAX_COMPOSER_HEIGHT}
            messageIdGenerator={() => uuidv4()}
            messages={messages}
            messagesContainerStyle={styles.messagesContainer}
            minComposerHeight={MIN_COMPOSER_HEIGHT}
            minInputToolbarHeight={MIN_INPUT_TOOLBAR_HEIGHT}
            onLoadEarlier={onLoadEarlier}
            onSend={async (draftMessages: Message[]) => {
              // Take the first message since only one is sent at a time anyway
              const draftMessage = draftMessages[0];

              setMessages((previousMessages) => [
                draftMessage,
                ...(previousMessages || []),
              ]);

              await sendMessage(draftMessage);
            }}
            renderAvatar={(props) => {
              const currentMessage = props.currentMessage as Message;
              const containerStyle: ViewStyle[] = [styles.baseBubble];

              if (currentMessage.user._id === CASEY_ID) {
                return (
                  <CaseyAvatar
                    containerStyle={containerStyle}
                    isLoading={
                      props.currentMessage?._id === LOADING_MESSAGE_ID &&
                      isSendingChatbotMessage
                    }
                  />
                );
              }

              const avatarProps: AvatarProps = {
                name: String(currentMessage.user.name || '--'),
                size: 'small',
                uuid: String(currentMessage.user._id),
              };

              // If supplier message, set avatar to color of supplier
              if (supplierUserMap[currentMessage.user._id]) {
                containerStyle.push(styles.supplierBubble);
              } else if (currentMessage.user._id === PEPPER_SUPPORT_UUID) {
                // If pepper message, use primary color
                containerStyle.push(styles.pepperBubble);
              }

              return (
                <Avatar {...avatarProps} containerStyle={containerStyle} />
              );
            }}
            renderBubble={(props: BubbleProps<Message>) => {
              const currentMessage = props.currentMessage;
              if (currentMessage?._id === LOADING_MESSAGE_ID) {
                return null;
              }
              return (
                <ChatBubble
                  {...props}
                  onCreditRequestPress={(creditRequestBatchUUID) => {
                    if (isSmallLayout) {
                      navigate(APP_CONTAINER_ROUTES.CreditRequestDetail, {
                        creditRequestBatchUUID,
                      });
                    } else {
                      navigate(APP_ROUTES.Orders, {
                        params: { creditRequestUUID: creditRequestBatchUUID },
                        screen: ORDER_ROUTES.Orders,
                      });
                    }
                  }}
                  onOrderPress={(orderUUID, customerFacingID) => {
                    if (isSmallLayout) {
                      navigate(APP_CONTAINER_ROUTES.OrderDetail, {
                        orderUUID,
                      });
                    } else {
                      setSelectedOrder({ customerFacingID, uuid: orderUUID });
                    }
                  }}
                  onRetrySend={async (draftMessage) => {
                    // Reset error state
                    updateMessage(draftMessage._id, (previousMessage) => {
                      previousMessage.error = null;
                      return previousMessage;
                    });

                    await sendMessage(draftMessage);
                  }}
                />
              );
            }}
            renderChatFooter={() =>
              isDisplayingPrompts ? (
                <CannedQuestionListLayoutContainer>
                  <CannedQuestionList
                    maxSize={4}
                    numColumns={isSmallTabletLayout ? 2 : undefined}
                    onQuestionSelected={onPressPrompt}
                  />
                </CannedQuestionListLayoutContainer>
              ) : messages.length === 0 ? (
                <ChatEmptyCard />
              ) : null
            }
            renderDay={(props: DayProps) => <ChatDay {...props} />}
            renderInputToolbar={(props) => (
              <ChatInputToolbar
                {...props}
                isCaseyAi={isCaseyAi}
                isSendDisabled={isSendingChatbotMessage}
                onPressPrompt={onPressPrompt}
                setShowCannedQuestionActionSheet={
                  setShowCannedQuestionActionSheet
                }
                showCannedQuestionActionSheet={showCannedQuestionActionSheet}
              />
            )}
            renderMessage={(props: MessageProps<Message>) => (
              <ChatMessage {...props} />
            )}
            renderMessageImage={(props: MessageImageProps<Message>) =>
              // This only supports PDF files for now and can probably be
              // expanded to other types later
              props.currentMessage?.image?.toLowerCase().endsWith('.pdf') ? (
                <ChatMessageFile {...props} />
              ) : (
                <ChatMessageImage {...props} />
              )
            }
            renderMessageText={(props: MessageTextProps<Message>) => (
              <ChatMessageText {...props} />
            )}
            renderSystemMessage={(props: SystemMessageProps<Message>) => (
              <ChatSystemMessage {...props} />
            )}
            // @ts-ignore - react-native-gifted-chat has a type error
            renderTicks={() => null}
            // Time is rendered by each message type
            renderTime={() => null}
            user={user}
            wrapInSafeArea={false}
          />
        )}
      </View>
      <BaseActionSheet
        contentStyle={styles.actionSheetContent}
        isVisible={Boolean(selectedOrder)}
        modalStyle={styles.actionSheetModal}
        onClose={() => setSelectedOrder(null)}
        title={
          selectedOrder
            ? i18n.t('order_title_next', {
                customerFacingId: selectedOrder.customerFacingID,
              })
            : ''
        }
      >
        {selectedOrder ? (
          <OrderDetailComponent
            hideTitle
            invoiceButtonPosition="top"
            navigationHeaderContainerStyle={styles.orderDetailPadding}
            orderUUID={selectedOrder.uuid}
          />
        ) : null}
      </BaseActionSheet>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionSheetContent: {
    flex: 1,
    paddingHorizontal: 0,
  },
  actionSheetModal: {
    flex: 1,
    maxWidth: 820,
  },
  base: {
    backgroundColor: theme.colors.white,
    flex: 1,
  },
  baseBubble: {
    height: AVATAR_DIAMETER,
    width: AVATAR_DIAMETER,
  },
  loadingContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    flex: 1,
    justifyContent: 'center',
  },
  messagesContainer: {
    flexDirection: 'column-reverse',
  },
  orderDetailPadding: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  pepperBubble: {
    backgroundColor: theme.colors.primary,
  },
  supplierBubble: {
    backgroundColor: theme.colors.blueAttention,
  },
}));
